import { useEffect } from 'react'
import type { RefObject } from 'react'

/**
 * How tall the thing pinned to the foot of the viewport is, published as a
 * custom property on the document element.
 *
 * Two things dock down there - the secret drawer, and the action sheet above
 * it - and everything else on the screen has to know how much room they are
 * between them taking: the page has to end above them rather than behind
 * them, the hold hint has to clear them, and the sheet has to sit on top of
 * the drawer rather than across it.
 *
 * Measured rather than declared, which is the whole point of this file. Both
 * of those were constants to begin with, and both were wrong - the drawer's
 * cover is a glyph over a label and comes out a good twenty pixels taller than
 * the height reserved for it, so the sheet's bottom edge was being parked
 * underneath the drawer's top edge and the last control in the sheet was cut
 * in half. A number in a stylesheet cannot be told that a line of text wrapped.
 *
 * Written on the document element because one of the readers - the hold hint -
 * is mounted outside the shell entirely.
 */
export function useDockHeight(
  ref: RefObject<HTMLElement | null>,
  /** The custom property to publish into, `--`-prefixed. */
  property: string,
): void {
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const root = document.documentElement
    const measure = () => {
      /*
       * The stylesheet decides what is a dock and what is merely a card in a
       * column: a laptop lays the board out beside the controls and pins
       * nothing to the bottom of the window. Asking the element rather than
       * re-deriving the breakpoint in JS keeps one answer to that question.
       */
      const docked = window.getComputedStyle(el).position === 'fixed'
      const height = docked ? window.innerHeight - el.getBoundingClientRect().top : 0
      root.style.setProperty(property, `${Math.max(0, Math.ceil(height))}px`)
    }
    measure()
    /*
     * And again on the next frame, because these two measure each other. The
     * sheet sits on top of the drawer, so where the sheet starts depends on a
     * property the drawer has not necessarily published yet - sibling effects
     * run in tree order, and the sheet is above the drawer in the tree.
     */
    const frame = window.requestAnimationFrame(measure)
    // The docks change height constantly: a veil opens, a hand comes back a
    // tile shorter, the sheet's handle folds its body away entirely.
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    window.addEventListener('resize', measure)
    return () => {
      window.cancelAnimationFrame(frame)
      observer.disconnect()
      window.removeEventListener('resize', measure)
      root.style.removeProperty(property)
    }
  }, [ref, property])
}
